/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { isArray } from 'lodash';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Chip,
    alpha,
    Tooltip,
    IconButton,
    Popover,
    List,
    ListItemButton,
    ListItemText,
    Checkbox,
    Divider,
    Badge,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Iconify } from 'src/components';
import { parseApiDate } from 'src/redux/slices/analyticsReports';
import { DATE_TITLES } from 'src/constants/newLead';
import { useSelector as useReduxSelector, dispatch as reduxDispatch } from 'src/redux/store';
import {
    getTablePreferences,
    handleTablePreferences,
    handleTablePreferencesEdit,
    setTablePreferenceLoading,
} from 'src/redux/slices/tablePreferences';
import { getCurrentTableData } from 'src/utils/tablePreferences';
import useGetCurrentUserId from 'src/hooks/useGetCurrentUserId';
import useReportExportFilename from 'src/hooks/useReportExportFilename';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);

const TABLE_NAME = 'Lost Leads Table';

const ALL_COLUMNS = [
    { field: 'name', label: 'Name' },
    { field: 'treatment', label: 'Treatment' },
    { field: 'source', label: 'Source' },
    { field: 'reason', label: 'Reason' },
    { field: 'assignedTo', label: 'Assigned To' },
    { field: 'date', label: 'Archived Date' },
    { field: 'value', label: 'Value' },
];

const formatCurrency = (value) => {
    if (value >= 1000000) return `£${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `£${(value / 1000).toFixed(1)}K`;
    return `£${value?.toFixed(0) || 0}`;
};

// Parse an API date ("05-10-2026 18:44:43Z", ISO, ...) with the shared helper.
// dayjs() alone reads "05-10-2026" as 10 May, or fails on the trailing Z.
const parseDate = (dateStr) => {
    const d = parseApiDate(dateStr);
    return d && d.isValid() ? d.local() : null;
};

// Format date based on mode
const formatDate = (dateStr, mode) => {
    if (!dateStr) return '-';
    if (mode === 'human_readable') {
        const d = parseDate(dateStr);
        return d ? d.fromNow() : '-';
    }
    const d = parseDate(dateStr);
    if (!d) return '-';
    if (mode === 'date_time_format') return d.format('DD MMM YYYY, h:mm A');
    return d.format('DD MMM YYYY');
};

export default function LostLeadsTable({ data, reasonFilter, onReasonFilterChange }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [sortModel, setSortModel] = useState([{ field: 'date', sort: 'desc' }]);
    const [columnsAnchor, setColumnsAnchor] = useState(null);
    const [exportAnchor, setExportAnchor] = useState(null);
    const [hiddenColumns, setHiddenColumns] = useState([]);
    const [prefsLoaded, setPrefsLoaded] = useState(false);
    const [dateFormat, setDateFormat] = useState(() => localStorage.getItem('reportsDateFormat') || 'human_readable');
    const [dateFormatAnchor, setDateFormatAnchor] = useState(null);

    const handleDateFormatChange = (value) => {
        setDateFormat(value);
        localStorage.setItem('reportsDateFormat', value);
        setDateFormatAnchor(null);
    };

    // Table preferences from Redux
    const userId = useGetCurrentUserId();
    const buildExportName = useReportExportFilename();
    const { tablePreferences: tablePrefsRaw, globalTablePreferences } = useReduxSelector((state) => state.tablePreferences);
    const prefResults = tablePrefsRaw?.results || tablePrefsRaw || [];

    // Load user preferences on mount
    useEffect(() => {
        if (userId) {
            reduxDispatch(getTablePreferences({ practiceUserId: userId }));
        }
    }, [userId]);

    // Save preferences to API
    const savePreferences = useCallback((newHidden) => {
        if (!userId) return;
        reduxDispatch(setTablePreferenceLoading(true));
        const tableData = getCurrentTableData(prefResults, TABLE_NAME);
        const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
        const currentTableData = isArray(tableData) && tableData.length > 0 ? tableData : globalData;
        const currentPreferences = currentTableData?.[0]?.preferences || {};
        const updatedPrefs = { ...currentPreferences, hiddenColumns: newHidden };
        const payload = {
            body: { table_name: TABLE_NAME, preferences: updatedPrefs },
            practiceUserId: userId,
        };
        if (isArray(tableData) && tableData.length > 0) {
            reduxDispatch(handleTablePreferencesEdit({ ...payload, tableId: tableData[0].id }));
        } else {
            reduxDispatch(handleTablePreferences(payload));
        }
    }, [userId, prefResults, globalTablePreferences]);

    // Load saved preferences
    useEffect(() => {
        if (!prefsLoaded && userId) {
            const tableData = getCurrentTableData(prefResults, TABLE_NAME);
            const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
            const currentTableData = isArray(tableData) && tableData.length > 0 ? tableData : globalData;
            const prefs = currentTableData?.[0]?.preferences;
            if (prefs?.hiddenColumns && isArray(prefs.hiddenColumns)) {
                setHiddenColumns(prefs.hiddenColumns);
            }
            setPrefsLoaded(true);
        }
    }, [prefResults, globalTablePreferences, userId]);

    // Column visibility model for DataGrid
    const columnVisibilityModel = useMemo(() => {
        const model = {};
        hiddenColumns.forEach((field) => { model[field] = false; });
        return model;
    }, [hiddenColumns]);

    // Whether filter is active
    const isFilterActive = hiddenColumns.length > 0;

    // Toggle column visibility
    const handleToggleColumn = useCallback((field) => {
        const updated = hiddenColumns.includes(field)
            ? hiddenColumns.filter((f) => f !== field)
            : [...hiddenColumns, field];
        setHiddenColumns(updated);
        savePreferences(updated);
    }, [hiddenColumns, savePreferences]);

    const filteredData = useMemo(() => {
        let result = data;
        if (reasonFilter) {
            result = result.filter((row) => row.reason === reasonFilter);
        }
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter((row) =>
                row.name?.toLowerCase().includes(query) ||
                row.treatment?.toLowerCase().includes(query) ||
                row.reason?.toLowerCase().includes(query) ||
                row.assignedTo?.toLowerCase().includes(query)
            );
        }
        return result;
    }, [data, searchQuery, reasonFilter]);

    // Export CSV — exports exactly what the user currently sees (filtered view)
    const handleExportCSV = useCallback(() => {
        setExportAnchor(null);
        if (!filteredData || filteredData.length === 0) return;
        const visibleCols = ALL_COLUMNS.filter((c) => !hiddenColumns.includes(c.field));
        const headers = visibleCols.map((c) => c.label);
        const rows = filteredData.map((row) => visibleCols.map((col) => {
            if (col.field === 'date') return formatDate(row.date, dateFormat);
            if (col.field === 'value') return row.value > 0 ? formatCurrency(row.value) : '£0';
            if (col.field === 'reason') {
                if (!row.reason || row.reason === '-') return '-';
                return row.reason.split('_').map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
            }
            if (col.field === 'source') return row.source?.replace(/_/g, ' ')?.toLowerCase() || '-';
            return row[col.field] ?? '-';
        }));
        const csvContent = [headers, ...rows].map((r) => r.map((cell) => `"${cell}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = buildExportName('lost-leads', undefined);
        link.click();
        URL.revokeObjectURL(link.href);
    }, [filteredData, hiddenColumns, dateFormat, buildExportName]);

    const columns = useMemo(() => [
        {
            field: 'name',
            headerName: 'Name',
            flex: 1.2,
            renderCell: (params) => (
                <Typography sx={{ fontSize: '14px', fontWeight: 500, pl: 1 }}>{params.value}</Typography>
            ),
        },
        {
            field: 'treatment',
            headerName: 'Treatment',
            flex: 1,
            renderCell: (params) => (
                <Typography sx={{ fontSize: '13px' }}>{params.value}</Typography>
            ),
        },
        {
            field: 'source',
            headerName: 'Source',
            flex: 0.8,
            renderCell: (params) => (
                <Typography sx={{ fontSize: '13px', textTransform: 'capitalize' }}>
                    {params.value?.replace(/_/g, ' ')?.toLowerCase() || '-'}
                </Typography>
            ),
        },
        {
            field: 'reason',
            headerName: 'Reason',
            flex: 1,
            renderCell: (params) => {
                const label = params.value === '-' ? '-' : params.value?.split('_').map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
                return (
                    <Chip
                        label={label}
                        size="small"
                        sx={{
                            bgcolor: alpha('#ef4444', 0.1),
                            color: '#ef4444',
                            fontWeight: 600,
                            fontSize: '11px',
                            height: 24,
                        }}
                    />
                );
            },
        },
        {
            field: 'assignedTo',
            headerName: 'Assigned To',
            flex: 0.8,
            renderCell: (params) => (
                <Typography sx={{ fontSize: '13px' }}>{params.value || '-'}</Typography>
            ),
        },
        {
            field: 'date',
            headerName: 'Archived Date',
            flex: 0.8,
            renderHeader: () => (
                <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setDateFormatAnchor(e.currentTarget);
                    }}
                >
                    <Typography sx={{
                        fontWeight: 600,
                        fontSize: '12px',
                        color: '#64748b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                    }}>
                        Archived Date
                    </Typography>
                    <Iconify icon="mdi:calendar-filter-outline" width={15} height={15} sx={{ color: '#64748b' }} />
                </Box>
            ),
            renderCell: (params) => (
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    {formatDate(params.value, dateFormat)}
                </Typography>
            ),
        },
        {
            field: 'value',
            headerName: 'Value',
            flex: 0.7,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: params.value > 0 ? '#ef4444' : 'text.disabled' }}>
                    {params.value > 0 ? formatCurrency(params.value) : '-'}
                </Typography>
            ),
        },
    ], [dateFormat]);

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                overflow: 'hidden',
                mb: 3,
            }}
        >
            <Box
                sx={{
                    p: 2.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Tooltip title="All lost leads with details. Search and sort to find specific leads." placement="bottom" arrow
                    slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 } } }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                        <Typography sx={{ fontSize: '20px', fontWeight: 700 }}>Lost Leads Details</Typography>
                        <Iconify icon="mdi:information-outline" width={14} sx={{ color: '#94a3b8' }} />
                    </Box>
                </Tooltip>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <TextField
                        size="small"
                        placeholder="Search leads..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ width: 250 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Iconify icon="eva:search-outline" width={20} sx={{ color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                    {/* Column visibility */}
                    <Tooltip title="Show/hide columns" arrow>
                        <Badge
                            variant="dot"
                            invisible={!isFilterActive}
                            sx={{
                                '& .MuiBadge-badge': {
                                    bgcolor: '#3b82f6',
                                    width: 8,
                                    height: 8,
                                    minWidth: 8,
                                    top: 4,
                                    right: 4,
                                },
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={(e) => setColumnsAnchor(e.currentTarget)}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    border: '1px solid',
                                    borderColor: (isFilterActive || columnsAnchor) ? 'primary.main' : '#e2e8f0',
                                    borderRadius: '8px',
                                    bgcolor: columnsAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                    '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                                }}
                            >
                                <Iconify icon="eva:options-2-outline" width={16} sx={{ color: (isFilterActive || columnsAnchor) ? 'primary.main' : '#64748b' }} />
                            </IconButton>
                        </Badge>
                    </Tooltip>

                    {/* Export */}
                    <Tooltip title="Export data" arrow>
                        <IconButton
                            size="small"
                            onClick={(e) => setExportAnchor(e.currentTarget)}
                            sx={{
                                width: 32,
                                height: 32,
                                border: '1px solid',
                                borderColor: exportAnchor ? 'primary.main' : '#e2e8f0',
                                borderRadius: '8px',
                                bgcolor: exportAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                            }}
                        >
                            <Iconify icon="eva:download-outline" width={16} sx={{ color: exportAnchor ? 'primary.main' : '#64748b' }} />
                        </IconButton>
                    </Tooltip>

                    {/* Column visibility popover */}
                    <Popover
                        open={Boolean(columnsAnchor)}
                        anchorEl={columnsAnchor}
                        onClose={() => setColumnsAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', width: 240, maxHeight: 400 } } }}
                    >
                        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                Columns
                            </Typography>
                        </Box>
                        <List dense sx={{ py: 0.5 }}>
                            {ALL_COLUMNS.map((col) => {
                                const isVisible = !hiddenColumns.includes(col.field);
                                return (
                                    <ListItemButton
                                        key={col.field}
                                        onClick={() => handleToggleColumn(col.field)}
                                        sx={{ py: 0.25, px: 1.5, opacity: isVisible ? 1 : 0.6 }}
                                    >
                                        <Checkbox
                                            size="small"
                                            checked={isVisible}
                                            sx={{ p: 0, mr: 1 }}
                                        />
                                        <ListItemText
                                            primary={col.label}
                                            primaryTypographyProps={{ fontSize: '13px', color: isVisible ? '#1e293b' : '#94a3b8' }}
                                        />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Popover>

                    {/* Export popover */}
                    <Popover
                        open={Boolean(exportAnchor)}
                        anchorEl={exportAnchor}
                        onClose={() => setExportAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 200 } } }}
                    >
                        <List dense sx={{ py: 0.5 }}>
                            <ListItemButton onClick={handleExportCSV} sx={{ py: 1, px: 2 }}>
                                <Iconify icon="mdi:file-delimited-outline" width={18} sx={{ color: '#64748b', mr: 1.5 }} />
                                <ListItemText primary="Export CSV" primaryTypographyProps={{ fontSize: '13px' }} />
                            </ListItemButton>
                        </List>
                    </Popover>
                </Box>
            </Box>

            {/* Date format popover */}
            <Popover
                open={Boolean(dateFormatAnchor)}
                anchorEl={dateFormatAnchor}
                onClose={() => setDateFormatAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        sx: { mt: 0.5, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 140 },
                    },
                }}
            >
                <List dense sx={{ py: 0.5 }}>
                    {DATE_TITLES.map((option) => (
                        <ListItemButton
                            key={option.value}
                            onClick={() => handleDateFormatChange(option.value)}
                            selected={dateFormat === option.value}
                            sx={{ py: 1, px: 2 }}
                        >
                            <Iconify icon={option.icon} width={18} height={18} sx={{ mr: 1.5, color: dateFormat === option.value ? 'primary.main' : '#64748b' }} />
                            <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: '13px', fontWeight: dateFormat === option.value ? 600 : 400 }} />
                        </ListItemButton>
                    ))}
                </List>
            </Popover>

            {/* Reason filter chips */}
            {reasonFilter && (
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>Filtered by:</Typography>
                    <Chip
                        label={reasonFilter.split('_').map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ')}
                        size="small"
                        onDelete={() => onReasonFilterChange && onReasonFilterChange(null)}
                        sx={{
                            bgcolor: alpha('#ef4444', 0.1),
                            color: '#ef4444',
                            fontWeight: 600,
                            fontSize: '11px',
                            height: 24,
                        }}
                    />
                </Box>
            )}

            <Box>
                <DataGridPro
                    rows={filteredData}
                    columns={columns}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    columnVisibilityModel={columnVisibilityModel}
                    autoHeight
                    disableRowSelectionOnClick
                    disableColumnMenu={false}
                    pagination
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    getRowId={(row) => row.id ?? `${row.name}-${row.date}`}
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': { bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
                        '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, fontSize: '12px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' },
                        '& .MuiDataGrid-cell': { borderBottom: '1px solid #f1f5f9' },
                        '& .MuiDataGrid-row:hover': { bgcolor: '#f8fafc' },
                    }}
                />
            </Box>
        </Box>
    );
}

LostLeadsTable.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        treatment: PropTypes.string,
        source: PropTypes.string,
        reason: PropTypes.string,
        assignedTo: PropTypes.string,
        date: PropTypes.string,
        value: PropTypes.number,
    })),
    reasonFilter: PropTypes.string,
    onReasonFilterChange: PropTypes.func,
};

LostLeadsTable.defaultProps = {
    data: [],
    reasonFilter: null,
    onReasonFilterChange: null,
};
